import React from 'react';
import { useTranslation } from 'react-i18next';
import { Namespace } from '../../../../translation/namespace';
import { LoadingSpinner } from '../../../../components/atoms/LoadingSpinner';
import bem from '../../../../lib/bemUtils/bem';
import { Icons, SVG } from '../../../../components/atoms/SVG';
import { Modifier } from '../../../../lib/bemUtils/Modifier';

export type LoadingStatus = 'sending' | 'success' | 'fail' | undefined;

export type LoadMoreButtonType = {
  url?: string,
  maxResultsSize: number,
  fromResults: number,
  toResults: number,
};

type LoadMoreButtonProperties = LoadMoreButtonType & {
  onClick: () => void,
  loadingStatus: LoadingStatus,
  backToTop?: boolean,
  buttonContainerModifier?: Modifier<'no-border'>
};

export function LoadMoreButton({
  backToTop,
  maxResultsSize,
  fromResults,
  toResults,
  loadingStatus,
  onClick,
  buttonContainerModifier,
}: LoadMoreButtonProperties) {
  const { t } = useTranslation(Namespace.LoadMore);

  if (!maxResultsSize && !fromResults && !toResults) {
    return null;
  }

  return (
    <div className={bem('load-more__button-container', buttonContainerModifier)}>
      <button type="button" className="load-more__button" onClick={onClick}>
        <span className="load-more__button-text">
          {/* hiding unnecessary symbols causing screen readers to pause  */}
          {t('results')} {fromResults} - {toResults} <span aria-hidden="true">(</span>
          {t('of')} {maxResultsSize}<span aria-hidden="true">)</span> {t('show')}
        </span>
        <span className="load-more__button-icon">
          { backToTop ? <SVG svgSpriteId={Icons.ARROW_UP} /> : <SVG svgSpriteId={Icons.ARROW_DOWN} /> }
        </span>
      </button>
      <div className={bem('load-more__status', loadingStatus)}>
        {loadingStatus === 'sending'
          ? (
              <div className="load-more__icons-container">
                <LoadingSpinner />
              </div>
            )
          : null}
        {loadingStatus === 'fail'
          ? (
              <div className="load-more__icons-container">
                <SVG svgSpriteId={Icons.FEEDBACK_ERROR} />
              </div>
            )
          : null}
        <div className={bem('load-more__live-region', loadingStatus)} aria-live="polite">
          {loadingStatus !== undefined ? t(`loadingStatus.${loadingStatus}`) : null}
        </div>
      </div>
    </div>
  );
}
